// ───────────────────────────────────────────────────────────────
// src/store/programs/programs-slice.ts
// ───────────────────────────────────────────────────────────────
import { createSlice } from '@reduxjs/toolkit';
import { getDayProgramOperation, toggleProgramItemOperation } from './programs-operations';
import { DayProgram, DayProgramItem, DayProgramView, Section, Task } from './ProgramsTypes';
import T from '@data/text/myHealthTexts.json';

/* ────────────────────────────────────────────────────────────
   2. State
   ──────────────────────────────────────────────────────────── */
export interface ProgramsState {
  dayProgram: DayProgramView | null;
  loading: boolean;
  togglingItem: boolean;
  error: string | null;
}

const initialState: ProgramsState = {
  dayProgram: null,
  loading: false,
  togglingItem: false,
  error: null,
};

/* ────────────────────────────────────────────────────────────
   3. Helpers: raw→view-model
   ──────────────────────────────────────────────────────────── */
const RU_WEEKDAYS = new Intl.DateTimeFormat('ru-RU', { weekday: 'long' });

const sectionRu: Record<DayProgramItem['section'], Section['period']> = {
  morning: T.program.periodLabels.morning as Section['period'],
  day: T.program.periodLabels.day as Section['period'],
  evening: T.program.periodLabels.evening as Section['period'],
};

const getWeekOfMonth = (date: Date): number => {
  const firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);

  // Сдвиг от понедельника для 1-го числа
  const offset = (firstOfMonth.getDay() + 6) % 7; // Пн = 0 … Вс = 6

  //      день_месяца + смещение - 1         // сколько дней от «понедельника 0-й недели»
  //  ──────────────────────────────── ───── // делим на 7 → кол-во полных недель
  return Math.floor((date.getDate() + offset - 1) / 7) + 1;
};

// TODO: should be handled by backend
function mapToView(p: DayProgram): DayProgramView {
  /* группируем по section */
  const buckets: Record<DayProgramItem['section'], DayProgramItem[]> = {
    morning: [],
    day: [],
    evening: [],
  };
  p.items.forEach(i => buckets[i.section].push(i));

  const sections: Section[] = (Object.keys(buckets) as (keyof typeof buckets)[]).map(sec => ({
    period: sectionRu[sec],
    tasks: buckets[sec].map(t => ({
      item_id: t.item_id,
      type: t.item_type as Task['type'],
      title: t.label,
      is_completed: t.is_completed,
      note: t.item_type === 'vitamin' ? String(t.extra_json?.dose ?? '') : undefined,
      meta: t.item_type === 'exercise' ? String(t.extra_json?.prescription ?? '') : undefined,
      kcal: t.item_type === 'meal' ? Number(t.extra_json?.kcal ?? NaN) : undefined,
    })),
  }));

  return {
    date: p.date,
    dayOfWeek: RU_WEEKDAYS.format(new Date(p.date)),
    weekIdx: getWeekOfMonth(new Date(p.date)),
    completionPercent: p.completion_percent,
    sections,
  };
}
/* ────────────────────────────────────────────────────────────
   4. Slice
   ──────────────────────────────────────────────────────────── */
const programsSlice = createSlice({
  name: 'programs',
  initialState,
  reducers: {
    resetProgramsState: () => initialState,
  },
  extraReducers: builder => {
    /* —— GET /day-programs/{date} —— */
    builder
      .addCase(getDayProgramOperation.pending, s => {
        s.loading = true;
        s.error = null;
      })
      .addCase(getDayProgramOperation.fulfilled, (s, { payload }) => {
        s.dayProgram = mapToView(payload);
        s.loading = false;
      })
      .addCase(getDayProgramOperation.rejected, (s, { payload, error }) => {
        s.loading = false;
        s.error = payload?.message || error.message || null;
      });

    /* —— PATCH /day-programs/items/{id}?done=… —— */
    builder
      .addCase(toggleProgramItemOperation.pending, s => {
        s.togglingItem = true;
        s.error = null;
      })
      .addCase(toggleProgramItemOperation.fulfilled, (s, { payload }) => {
        s.dayProgram = mapToView(payload);
        s.togglingItem = false;
      })
      .addCase(toggleProgramItemOperation.rejected, (s, { payload, error }) => {
        s.togglingItem = false;
        s.error = payload?.message || error.message || null;
      });
  },
});

export const { resetProgramsState } = programsSlice.actions;
export default programsSlice.reducer;
